import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PlatformLogo } from "@/components/PlatformLogo";
import { Plus, Trash } from "@phosphor-icons/react";

const AUTH_TYPES = ["none", "api_key_header", "api_key_query", "bearer", "basic", "oauth2_client_credentials"];
const PAGINATION_TYPES = ["none", "offset", "page", "cursor", "link"];
const DISCOVERY_TYPES = ["sample", "describe", "declared"];

const clone = (o) => JSON.parse(JSON.stringify(o || {}));

function Field({ label, value, onChange, placeholder, type = "text", hint, testid }) {
  return (
    <div>
      <Label className="text-xs uppercase tracking-wider text-stone-500">{label}</Label>
      <Input data-testid={testid} type={type} value={value ?? ""} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} className="mt-1 rounded-xl text-sm" />
      {hint && <p className="text-[11px] text-stone-400 mt-1">{hint}</p>}
    </div>
  );
}

export default function ManifestBuilder({ value, onChange }) {
  const m = value;
  const set = (mutator) => { const next = clone(m); mutator(next); onChange(next); };

  const auth = m.auth || {};
  const setAuth = (k, v) => set((n) => { n.auth = { ...(n.auth || {}), [k]: v }; });
  const setAuthType = (t) => set((n) => { n.auth = { type: t }; });

  const cred = m.credential_fields || [];
  const objects = m.objects || [];

  return (
    <div className="space-y-6 max-h-[62vh] overflow-y-auto pr-2">
      {/* Basics */}
      <section className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-stone-900">Basics</h3>
          <PlatformLogo platform={m.platform} logoUrl={m.logo_url} baseUrl={m.base_url} size={22} />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field testid="mb-platform" label="Platform key" value={m.platform} placeholder="pipedrive"
            onChange={(v) => set((n) => { n.platform = v.toLowerCase().replace(/\s+/g, "_"); })}
            hint="Unique id, lowercase. Used to auto-resolve the logo too." />
          <Field testid="mb-label" label="Display name" value={m.label} placeholder="Pipedrive"
            onChange={(v) => set((n) => { n.label = v; })} />
          <Field testid="mb-category" label="Category" value={m.category} placeholder="crm"
            onChange={(v) => set((n) => { n.category = v; })} />
          <Field testid="mb-logo" label="Logo URL (optional)" value={m.logo_url}
            placeholder="auto from key if blank"
            onChange={(v) => set((n) => { n.logo_url = v; })} />
        </div>
        <Field testid="mb-baseurl" label="Base URL" value={m.base_url}
          placeholder="https://api.example.com/v1"
          onChange={(v) => set((n) => { n.base_url = v; })}
          hint="Use {placeholders} for per-account hosts, e.g. https://{subdomain}.zendesk.com/api/v2" />
      </section>

      {/* Auth */}
      <section className="space-y-3 border-t border-stone-100 pt-4">
        <h3 className="text-sm font-semibold text-stone-900">Authentication</h3>
        <div>
          <Label className="text-xs uppercase tracking-wider text-stone-500">Auth type</Label>
          <Select value={auth.type || "none"} onValueChange={setAuthType}>
            <SelectTrigger data-testid="mb-auth-type" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
            <SelectContent>{AUTH_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {auth.type === "api_key_header" && (<>
            <Field label="Header name" value={auth.header_name} placeholder="Authorization" onChange={(v) => setAuth("header_name", v)} />
            <Field label="Prefix" value={auth.prefix} placeholder="Bearer " onChange={(v) => setAuth("prefix", v)} />
            <Field label="Credential field" value={auth.value_field} placeholder="api_key" onChange={(v) => setAuth("value_field", v)} />
          </>)}
          {auth.type === "api_key_query" && (<>
            <Field label="Query param" value={auth.param_name} placeholder="api_token" onChange={(v) => setAuth("param_name", v)} />
            <Field label="Credential field" value={auth.value_field} placeholder="api_token" onChange={(v) => setAuth("value_field", v)} />
          </>)}
          {auth.type === "bearer" && (
            <Field label="Credential field" value={auth.value_field} placeholder="access_token" onChange={(v) => setAuth("value_field", v)} />
          )}
          {auth.type === "basic" && (<>
            <Field label="Username field" value={auth.username_field} placeholder="email" onChange={(v) => setAuth("username_field", v)} />
            <Field label="Username suffix" value={auth.username_suffix} placeholder="/token (Zendesk)" onChange={(v) => setAuth("username_suffix", v)} />
            <Field label="Password field" value={auth.password_field} placeholder="api_token" onChange={(v) => setAuth("password_field", v)} />
          </>)}
          {auth.type === "oauth2_client_credentials" && (<>
            <Field label="Token URL" value={auth.token_url} placeholder="https://.../oauth/token" onChange={(v) => setAuth("token_url", v)} />
            <Field label="Scope (optional)" value={auth.scope} onChange={(v) => setAuth("scope", v)} />
            <Field label="Client id field" value={auth.client_id_field} placeholder="client_id" onChange={(v) => setAuth("client_id_field", v)} />
            <Field label="Client secret field" value={auth.client_secret_field} placeholder="client_secret" onChange={(v) => setAuth("client_secret_field", v)} />
          </>)}
        </div>

        {/* Credential fields */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs uppercase tracking-wider text-stone-500">Credential fields (what the user enters)</Label>
            <Button data-testid="mb-add-cred" type="button" size="sm" variant="outline" className="rounded-xl gap-1 text-xs h-7"
              onClick={() => set((n) => { n.credential_fields = [...(n.credential_fields || []), { name: "", label: "", secret: false, required: true }]; })}>
              <Plus size={12} /> Add field
            </Button>
          </div>
          {cred.map((c, i) => (
            <div key={i} className="flex items-center gap-2">
              <Input placeholder="name" value={c.name || ""} className="rounded-xl text-xs h-8"
                onChange={(e) => set((n) => { n.credential_fields[i].name = e.target.value; })} />
              <Input placeholder="label" value={c.label || ""} className="rounded-xl text-xs h-8"
                onChange={(e) => set((n) => { n.credential_fields[i].label = e.target.value; })} />
              <label className="flex items-center gap-1 text-xs text-stone-500 shrink-0">
                <input type="checkbox" checked={!!c.secret} onChange={(e) => set((n) => { n.credential_fields[i].secret = e.target.checked; })} /> secret
              </label>
              <Button type="button" size="sm" variant="ghost" className="rounded-xl text-rose-600 h-8"
                onClick={() => set((n) => { n.credential_fields.splice(i, 1); })}><Trash size={13} /></Button>
            </div>
          ))}
        </div>
      </section>

      {/* Health check */}
      <section className="space-y-3 border-t border-stone-100 pt-4">
        <h3 className="text-sm font-semibold text-stone-900">Health check (Test connection)</h3>
        <div className="grid grid-cols-2 gap-3">
          <Field testid="mb-test-path" label="Test path" value={m.test?.path} placeholder="/users/me"
            onChange={(v) => set((n) => { n.test = { ...(n.test || {}), path: v, method: n.test?.method || "GET" }; })} />
          <Field label="Method" value={m.test?.method || "GET"}
            onChange={(v) => set((n) => { n.test = { ...(n.test || {}), method: v }; })} />
        </div>
      </section>

      {/* Objects */}
      <section className="space-y-3 border-t border-stone-100 pt-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold text-stone-900">Objects (the records you can migrate)</h3>
          <Button data-testid="mb-add-object" type="button" size="sm" variant="outline" className="rounded-xl gap-1 text-xs h-7"
            onClick={() => set((n) => {
              n.objects = [...(n.objects || []), {
                name: "", label: "", list: { path: "", method: "GET", records_path: "" },
                id_field: "id", pagination: { type: "none" }, discovery: { type: "sample" },
              }];
            })}>
            <Plus size={12} /> Add object
          </Button>
        </div>
        {objects.map((o, i) => (
          <div key={i} data-testid={`mb-object-${i}`} className="border border-stone-200 rounded-2xl shadow-card p-3 space-y-3 bg-stone-50/50">
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono text-stone-500">Object #{i + 1}</span>
              <Button type="button" size="sm" variant="ghost" className="rounded-xl text-rose-600 h-7"
                onClick={() => set((n) => { n.objects.splice(i, 1); })}><Trash size={13} /></Button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Object name" value={o.name} placeholder="persons"
                onChange={(v) => set((n) => { n.objects[i].name = v; })} />
              <Field label="Label" value={o.label} placeholder="Persons"
                onChange={(v) => set((n) => { n.objects[i].label = v; })} />
              <Field label="List path" value={o.list?.path} placeholder="/persons"
                onChange={(v) => set((n) => { n.objects[i].list = { ...(n.objects[i].list || {}), path: v, method: "GET" }; })} />
              <Field label="Records path" value={o.list?.records_path} placeholder="data (blank if API returns an array)"
                onChange={(v) => set((n) => { n.objects[i].list = { ...(n.objects[i].list || {}), records_path: v }; })} />
              <Field label="ID field" value={o.id_field} placeholder="id"
                onChange={(v) => set((n) => { n.objects[i].id_field = v; })} />
            </div>

            {/* Create */}
            <div className="flex items-center gap-2">
              <Switch checked={!!o.create} onCheckedChange={(v) => set((n) => {
                n.objects[i].create = v ? { path: n.objects[i].list?.path || "", method: "POST" } : undefined;
              })} />
              <span className="text-xs text-stone-600">Writable (supports create)</span>
            </div>
            {o.create && (
              <div className="grid grid-cols-2 gap-3">
                <Field label="Create path" value={o.create.path} placeholder="/persons"
                  onChange={(v) => set((n) => { n.objects[i].create.path = v; })} />
                <Field label="Wrap key (optional)" value={o.create.wrap} placeholder="user (Zendesk wraps body)"
                  onChange={(v) => set((n) => { n.objects[i].create.wrap = v || undefined; })} />
              </div>
            )}

            {/* Pagination */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">Pagination</Label>
                <Select value={o.pagination?.type || "none"} onValueChange={(v) => set((n) => { n.objects[i].pagination = { type: v }; })}>
                  <SelectTrigger className="mt-1 rounded-xl text-xs h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>{PAGINATION_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-stone-500">Field discovery</Label>
                <Select value={o.discovery?.type || "sample"} onValueChange={(v) => set((n) => { n.objects[i].discovery = { type: v }; })}>
                  <SelectTrigger className="mt-1 rounded-xl text-xs h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>{DISCOVERY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {o.discovery?.type === "describe" && (
              <Field label="Describe endpoint (returns field list)" value={o.discovery.path} placeholder="/personFields"
                onChange={(v) => set((n) => { n.objects[i].discovery.path = v; n.objects[i].discovery.records_path = n.objects[i].discovery.records_path || "data"; })} />
            )}
          </div>
        ))}
      </section>

      {/* Trial */}
      <section className="space-y-3 border-t border-stone-100 pt-4">
        <div className="flex items-center gap-2">
          <Switch data-testid="mb-trial" checked={!!m.trial?.enabled}
            onCheckedChange={(v) => set((n) => { n.trial = { ...(n.trial || {}), enabled: v }; })} />
          <span className="text-sm text-stone-700">Offer a trial / sandbox (connect with no credentials)</span>
        </div>
        {m.trial?.enabled && (
          <Field label="Trial note" value={m.trial?.note} placeholder="Public demo — no credentials required"
            onChange={(v) => set((n) => { n.trial = { ...(n.trial || {}), note: v }; })} />
        )}
      </section>
    </div>
  );
}
